import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AppendRemediationItemsRequest,
  AppendRemediationItemsResponse,
  GetRemediationItemsResponse,
  GetRemediationRunResponse,
  GetRemediationScopeResponse,
  ListActiveRemediationRunsResponse,
  ListRemediationRunStatusesResponse,
  RemediationMessageResponse,
  ResolveFailedRemediationItemResponse,
  ReviewRemediationChangeRequest,
  ReviewRemediationItemRequest,
  SaveRemediationPlaceholdersResponse,
  TriggerRemediationRequest,
  TriggerRemediationResponse,
} from '@libs/types';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ACTIVE_REMEDIATION_RUN_STATUSES, RemediationRunStatus } from '@libs/constants';

import { apiClient } from '../../../api/client';
import { queryKeys } from '../../../constants/query-keys';
import { useBackendErrorToast } from '../../../hooks/useBackendErrorToast';
import { diffRunStatuses, mayHaveForkedVersion } from '../utils/run-status-sync';

/**
 * Document Remediation API.
 *
 * Mirrors the api service's `RemediationController`. Every response shape comes
 * from `@libs/types` so the two sides cannot drift.
 *
 * Polling: a run moves PENDING → PROCESSING → READY_FOR_REVIEW → FINALIZING →
 * COMPLETED entirely on the worker, with no push channel, so the run query
 * self-polls while the status is non-terminal and stops as soon as it settles.
 */

// ── How often to re-poll a run that is still working. ────────────────────────
// The AI job takes minutes, not seconds, so a tight interval would just add load
// without the user noticing a difference. FINALIZING is renders + a version fork
// (fast), so it polls harder than generation.
const POLL_MS_GENERATING = 8_000;
const POLL_MS_FINALIZING = 4_000;

// ── How often the hub re-asks which runs are still working. ──────────────────
// Matches the assessment status poll's cadence, and is affordable at that rate
// for the same reason: `runs/status` is three fields per active run, with no
// item tallies, no assessment titles and nothing from the tenant's history.
// Asking `GET /remediation/runs` this often would not be.
const POLL_MS_RUN_STATUSES = 5_000;

/**
 * One page of a run's documents. Server-side: the run screen shows ten rows of a
 * list that can hold dozens, and this endpoint is polled every few seconds while
 * a run works — so paging in the browser meant re-downloading every item on
 * every tick to render a tenth of them.
 */
export interface RunItemsPaging {
  page: number;
  limit: number;
}

const isActive = (status: RemediationRunStatus | undefined): boolean =>
  status !== undefined &&
  (ACTIVE_REMEDIATION_RUN_STATUSES as readonly RemediationRunStatus[]).includes(status);

// =============================================================================
// Service functions
// =============================================================================

async function fetchRemediationScope(
  assessmentId: string,
  signal?: AbortSignal,
): Promise<GetRemediationScopeResponse> {
  const { data } = await apiClient.get<GetRemediationScopeResponse>(
    `api/assessments/${assessmentId}/remediation/scope`,
    { signal },
  );
  return data;
}

async function triggerRemediation({
  assessmentId,
  ...selection
}: TriggerRemediationRequest): Promise<TriggerRemediationResponse> {
  const { data } = await apiClient.post<TriggerRemediationResponse>(
    `api/assessments/${assessmentId}/remediation`,
    selection,
  );
  return data;
}

async function fetchRuns(
  statuses?: readonly RemediationRunStatus[],
  includeItems = true,
  signal?: AbortSignal,
): Promise<ListActiveRemediationRunsResponse> {
  const { data } = await apiClient.get<ListActiveRemediationRunsResponse>('api/remediation/runs', {
    signal,
    params: {
      // Repeated `status=` params, which is what the endpoint's DTO expects.
      ...(statuses?.length ? { status: statuses } : {}),
      // Only sent when opting out: the endpoint's default is true.
      ...(includeItems ? {} : { includeItems: false }),
    },
    paramsSerializer: { indexes: null },
  });
  return data;
}

/**
 * Which runs are still working, and at what status. Takes no parameters — the
 * endpoint answers for the caller's tenant and returns only the active runs.
 */
async function fetchRunStatuses(
  signal?: AbortSignal,
): Promise<ListRemediationRunStatusesResponse> {
  const { data } = await apiClient.get<ListRemediationRunStatusesResponse>(
    'api/remediation/runs/status',
    { signal },
  );
  return data;
}

async function fetchRun(
  runId: string,
  page?: number,
  limit?: number,
  signal?: AbortSignal,
): Promise<GetRemediationRunResponse> {
  const { data } = await apiClient.get<GetRemediationRunResponse>(`api/remediation/runs/${runId}`, {
    signal,
    // Only sent when a caller wants a page — the endpoint returns every item
    // otherwise, which is what the review screen relies on.
    params: page !== undefined || limit !== undefined ? { page, limit } : undefined,
  });
  return data;
}

async function fetchRunItems(
  runId: string,
  signal?: AbortSignal,
): Promise<GetRemediationItemsResponse> {
  const { data } = await apiClient.get<GetRemediationItemsResponse>(
    `api/remediation/runs/${runId}/items`,
    { signal },
  );
  return data;
}

/**
 * One figure's bytes, through the authenticated client.
 *
 * Not an `<img src>` pointing straight at the endpoint: the API is Bearer-authed
 * and a browser image request carries no Authorization header, so the direct URL
 * would 401. Fetching the blob here and handing the component an object URL is
 * what lets an authenticated route render as an image at all.
 */
async function fetchRunFigure(runId: string, key: string, signal?: AbortSignal): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`api/remediation/runs/${runId}/figures`, {
    params: { key },
    responseType: 'blob',
    signal,
  });
  return data;
}

async function reviewItem({
  itemId,
  decision,
}: ReviewRemediationItemRequest): Promise<RemediationMessageResponse> {
  const { data } = await apiClient.patch<RemediationMessageResponse>(
    `api/remediation/items/${itemId}/review`,
    { decision },
  );
  return data;
}

async function reviewChange({
  itemId,
  changeId,
  decision,
}: ReviewRemediationChangeRequest): Promise<RemediationMessageResponse> {
  const { data } = await apiClient.patch<RemediationMessageResponse>(
    `api/remediation/items/${itemId}/changes/${encodeURIComponent(changeId)}`,
    { decision },
  );
  return data;
}

async function appendRemediationItems({
  runId,
  ...selection
}: AppendRemediationItemsRequest & { runId: string }): Promise<AppendRemediationItemsResponse> {
  const { data } = await apiClient.post<AppendRemediationItemsResponse>(
    `api/remediation/runs/${runId}/items`,
    selection,
  );
  return data;
}

async function resolveFailedItem({
  runId,
  itemId,
  action,
}: {
  runId: string;
  itemId: string;
  action: 'retry' | 'remove';
}): Promise<ResolveFailedRemediationItemResponse> {
  const { data } = await apiClient.post<ResolveFailedRemediationItemResponse>(
    `api/remediation/runs/${runId}/items/${itemId}/resolve`,
    { action },
  );
  return data;
}

async function savePlaceholders({
  runId,
  values,
}: {
  runId: string;
  values: Record<string, string>;
}): Promise<SaveRemediationPlaceholdersResponse> {
  const { data } = await apiClient.patch<SaveRemediationPlaceholdersResponse>(
    `api/remediation/runs/${runId}/placeholders`,
    { values },
  );
  return data;
}

async function finalizeRun(runId: string): Promise<RemediationMessageResponse> {
  const { data } = await apiClient.post<RemediationMessageResponse>(
    `api/remediation/runs/${runId}/finalize`,
  );
  return data;
}

// =============================================================================
// Cache invalidation
// =============================================================================

/**
 * What a mutation changed, rather than which cache keys to bust.
 *
 * Every mutation used to hand-list its own `invalidateQueries` calls, and they
 * disagreed: the two review mutations left the tenant-wide runs list alone,
 * which is wrong, because that list's per-run `itemCounts` are derived from the
 * same item rows a decision moves. The list went on offering "Review 3
 * documents" for a run whose last document had just been decided.
 *
 * Naming the change instead of the keys is what stops that recurring - a caller
 * cannot forget a key it never names.
 */
function useRemediationInvalidation() {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const drop = (queryKey: readonly unknown[]) => {
      void queryClient.invalidateQueries({ queryKey });
    };

    return {
      /**
       * An item's status, content or membership moved.
       *
       * Takes the runs list with it, always: that list derives its counts from
       * these same rows, so there is no such thing as a document change which
       * leaves it accurate.
       */
      documentsChanged: (runId: string) => {
        // The prefix, not `run(runId)`: that key is per-page now, so busting it
        // alone would refresh the unpaged entry the review screen holds and leave
        // whichever page the run screen is showing stale. The prefix also covers
        // `runItems`, which sits beneath it.
        drop(queryKeys.remediation.runPrefix(runId));
        drop(queryKeys.remediation.runsAll);
      },
      /** Something on the run row itself, with no item touched. */
      runChanged: (runId: string) => drop(queryKeys.remediation.runPrefix(runId)),
      /** The candidates an assessment still offers - starting or appending moves one out. */
      scopeChanged: (assessmentId: string) => drop(queryKeys.remediation.scope(assessmentId)),
      /** The runs list is stale — a row appeared, left, or its counts moved. */
      runsListChanged: () => drop(queryKeys.remediation.runsAll),
      /**
       * The worker now owes something on a run — it was started, finalized, or
       * handed a retry from this tab.
       *
       * Wakes the hub's watcher as well as refreshing the list. The watcher stops
       * polling once nothing will move on its own, and its key sits outside
       * `runsAll` on purpose (see the key's comment), so without naming it here
       * the screen that just created the work would be the one not watching it.
       */
      workDispatched: () => {
        drop(queryKeys.remediation.runsAll);
        drop(queryKeys.remediation.runStatuses);
      },
      /**
       * A finalize landed, so a new assessment version now exists.
       *
       * Named rather than left to the observer that noticed, because this is the
       * one remediation event that reaches outside the feature: finalizing renders
       * the accepted documents and forks version N+1, so the assessment rows on
       * screen are wrong twice over — the version they show is no longer the
       * latest, and its successor is a PROCESSING assessment that was in no
       * earlier fetch. That is the same conclusion `useAssessmentStatusPolling`
       * reaches on a terminal grade, and it belongs here with the rest of the
       * feature's cache policy rather than inside whichever hook saw it happen.
       */
      versionForked: () => {
        drop(queryKeys.remediation.runsAll);
        drop(queryKeys.assessments.allPrefix);
      },
    };
  }, [queryClient]);
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Pre-flight scope for a completed assessment. 400s when the assessment is not
 * completed or has no gaps, which callers surface as "nothing to remediate"
 * rather than as an error.
 */
export const useRemediationScope = (assessmentId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.remediation.scope(assessmentId ?? ''),
    queryFn: ({ signal }) => fetchRemediationScope(assessmentId as string, signal),
    enabled: Boolean(assessmentId),
    retry: false,
  });

/**
 * Tenant-wide runs. Defaults to the in-flight ones — the "work in progress"
 * list — and takes an explicit status set when the caller also needs finished
 * runs, e.g. to tell whether an assessment has already been remediated.
 */
export const useActiveRemediationRuns = (
  enabled = true,
  statuses?: readonly RemediationRunStatus[],
  /**
   * Whether each run should carry its items. Off is for callers that only need
   * the runs — see the response type: asking for finished runs means asking for
   * the tenant's whole history, and the items are most of that payload.
   */
  includeItems = true,
) =>
  useQuery({
    queryKey: queryKeys.remediation.runs(statuses, includeItems),
    queryFn: ({ signal }) => fetchRuns(statuses, includeItems, signal),
    enabled,
  });

/**
 * Keep a mounted runs list current while anything is still working.
 *
 * Polls the slim `runs/status` endpoint rather than re-reading the list it is
 * refreshing: the list carries every run the tenant has ever had, plus item
 * tallies and assessment titles, none of which change while a run works.
 *
 * What it does with each tick depends on what moved:
 * - a status changed, same runs → patch the cached lists in place, no request;
 * - a run appeared or settled → re-read the list, because a new run needs its
 *   title and lineage and a settled one needs its final counts, and the status
 *   payload carries neither.
 *
 * Stops on its own: the endpoint returns only runs that can still change, so an
 * empty response means there is nothing left to watch. `useRemediationInvalidation`
 * wakes it again when a run is started from this tab.
 */
export const useRemediationRunStatusWatch = () => {
  const queryClient = useQueryClient();
  const invalidate = useRemediationInvalidation();

  const { data } = useQuery({
    queryKey: queryKeys.remediation.runStatuses,
    queryFn: ({ signal }) => fetchRunStatuses(signal),
    refetchInterval: (query) => {
      // Stopped on error rather than left ticking. A failed fetch leaves the
      // last good `data` in place, so a length check alone would go on seeing
      // active runs and hammer a failing endpoint forever — on a screen that
      // never renders this query's error. Self-healing: the mount refetch and
      // refetchOnWindowFocus are both on, and a success restarts the timer.
      if (query.state.status === 'error') return false;
      // A length check is the whole stop rule, because the endpoint answers only
      // for runs the worker is working on. Anything it returns will move without
      // the user doing something; an empty response means nothing will.
      return (query.state.data?.runs.length ?? 0) > 0 ? POLL_MS_RUN_STATUSES : false;
    },
  });

  const seen = useRef<Map<string, RemediationRunStatus> | null>(null);

  useEffect(() => {
    if (!data) return;

    const now = new Map(data.runs.map((run) => [run.runId, run.status]));
    const before = seen.current;
    seen.current = now;
    // The first payload is not a transition. Without this a hub opened while a
    // run was already working would read its own first read as news and refetch
    // everything on mount.
    if (!before) return;

    const { settled, appeared, moved } = diffRunStatuses(before, now);

    // Membership changed, which patching cannot express: a settled run needs the
    // counts that say how it went, a new run needs its title and lineage root.
    // Only the list itself has those.
    // A run stopped being worked on. Patching cannot express that: what it turned
    // into — the outcome counts behind "Review 3 documents", the final state of
    // the row — lives only in the full list. That re-read is also what makes the
    // counts right at the one moment the user is about to act on them.
    if (settled) {
      if (mayHaveForkedVersion(before, now)) invalidate.versionForked();
      else invalidate.runsListChanged();
    } else if (appeared) invalidate.runsListChanged();
    else if (moved) {
      // Same runs, new statuses. Patch every runs-list variant in place so the
      // badge advances without re-downloading the tenant's run history — the
      // `setQueryData` step of the assessment status poll, widened from one row
      // to whichever lists are cached.
      queryClient.setQueriesData<ListActiveRemediationRunsResponse>(
        { queryKey: queryKeys.remediation.runsAll },
        (old) => {
          if (!old) return old;
          let touched = false;
          const runs = old.runs.map((run) => {
            const status = now.get(run.runId);
            if (status === undefined || status === run.status) return run;
            touched = true;
            return { ...run, status };
          });
          // The same reference back when this particular list held none of the
          // runs that moved — a fresh object would re-render its subscribers to
          // show them exactly what they already had.
          return touched ? { ...old, runs } : old;
        },
      );
    }
  }, [data, queryClient, invalidate]);
};

/**
 * One run's status + item summaries. Self-polls while the run is still active so
 * the progress screen advances without a manual refresh, and stops the moment it
 * reaches COMPLETED / FAILED / DISCARDED.
 *
 * Pass `paging` to get one page of documents plus `itemsMeta`; omit it to get
 * every item, which is what a caller reading only the run's status wants. The
 * run's counts (`itemCounts`, `itemStatusCounts`) are whole-run either way, so a
 * paged caller can still show whole-run totals above a page of rows.
 */
export const useRemediationRun = (runId: string | undefined, paging?: RunItemsPaging) => {
  // Destructured rather than passed through as an object: the paging is part of
  // the cache key, and an inline object literal would be a new reference on
  // every render — a fresh key each time, which is a refetch loop.
  const { page, limit } = paging ?? {};

  return useQuery({
    queryKey: queryKeys.remediation.run(runId ?? '', page, limit),
    queryFn: ({ signal }) => fetchRun(runId as string, page, limit, signal),
    enabled: Boolean(runId),
    // Each page is its own cache entry, so without this a page flip would empty
    // the list and re-run the skeleton before the next page landed — on a screen
    // that is also polling, which would make it flicker on its own.
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!isActive(status)) return false;
      return status === RemediationRunStatus.FINALIZING ? POLL_MS_FINALIZING : POLL_MS_GENERATING;
    },
  });
};

/**
 * An object URL for one figure, created when its bytes arrive and revoked when
 * the component unmounts or moves to another figure.
 *
 * `staleTime: Infinity` because the storage key embeds a content hash: the bytes
 * behind a given key cannot change, so a refetch could only ever return the same
 * image — and each refetch would mint a new object URL and flicker the picture.
 */
export const useRemediationFigure = (runId: string | undefined, key: string | null | undefined) => {
  const query = useQuery({
    queryKey: queryKeys.remediation.figure(runId ?? '', key ?? ''),
    queryFn: ({ signal }) => fetchRunFigure(runId as string, key as string, signal),
    enabled: Boolean(runId) && Boolean(key),
    staleTime: Infinity,
    retry: false,
  });

  const blob = query.data;
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    // Revoked on the way out — an object URL pins its blob in memory for the
    // lifetime of the document otherwise, and a review screen walks through
    // many figures.
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  return { url: objectUrl, isLoading: query.isLoading, isError: query.isError };
};

/**
 * The full review payload. Only worth fetching once the run is open for review —
 * before that every item's `generatedContent` is still null.
 *
 * NOTE: this returns every item's full document plus its raw `sourceText` in one
 * response. There is no per-item endpoint yet; see the frontend gaps document.
 */
export const useRemediationRunItems = (
  runId: string | undefined,
  enabled = true,
  /**
   * Keep polling while the run is still generating. Documents settle one at a
   * time, so a reviewer who started early should see the rest arrive rather
   * than have to reload.
   */
  stillGenerating = false,
) =>
  useQuery({
    queryKey: queryKeys.remediation.runItems(runId ?? ''),
    queryFn: ({ signal }) => fetchRunItems(runId as string, signal),
    enabled: Boolean(runId) && enabled,
    refetchInterval: stillGenerating ? 8_000 : false,
  });

// =============================================================================
// Mutations
// =============================================================================

/**
 * Start a run for a completed assessment. 409s when the lineage already has an
 * active run or a draft version — the scope response's `activeRun` is what the
 * UI uses to avoid offering the action in the first place.
 */
export const useTriggerRemediation = () => {
  const invalidate = useRemediationInvalidation();
  const onError = useBackendErrorToast('app.remediation.toast.startFailed');

  return useMutation({
    mutationFn: triggerRemediation,
    onSuccess: (_data, { assessmentId }) => {
      invalidate.scopeChanged(assessmentId);
      invalidate.workDispatched();
    },
    onError,
  });
};

/** Whole-document accept/reject. GENERATE items only. */
export const useReviewRemediationItem = (runId: string) => {
  const invalidate = useRemediationInvalidation();
  const onError = useBackendErrorToast('app.remediation.toast.reviewFailed');

  return useMutation({
    mutationFn: reviewItem,
    onSuccess: () => invalidate.documentsChanged(runId),
    onError,
  });
};

/**
 * Per-change accept/reject. REMEDIATE items only. Once every change in an item
 * is decided the backend resolves the item to ACCEPTED (≥1 accepted) or
 * REJECTED, which is why this invalidates the run summary too.
 */
export const useReviewRemediationChange = (runId: string) => {
  const invalidate = useRemediationInvalidation();
  const onError = useBackendErrorToast('app.remediation.toast.reviewFailed');

  return useMutation({
    mutationFn: reviewChange,
    onSuccess: () => invalidate.documentsChanged(runId),
    onError,
  });
};

/**
 * Add documents to a run that is already in flight.
 *
 * 409s once the run is terminal — its documents are already folded into a new
 * assessment version, or it was abandoned — which is why the caller only offers
 * this while the run is still working or waiting to be reviewed.
 *
 * Invalidates the scope alongside the run: the scope response is what the picker
 * lists candidates from, and appending is what moves one out of that list.
 */
export const useAppendRemediationItems = (runId: string, assessmentId: string) => {
  const invalidate = useRemediationInvalidation();
  const onError = useBackendErrorToast('app.remediation.toast.addFailed');

  return useMutation({
    mutationFn: appendRemediationItems,
    onSuccess: () => {
      invalidate.documentsChanged(runId);
      invalidate.scopeChanged(assessmentId);
    },
    onError,
  });
};

/**
 * Retry or remove a document the AI could not produce.
 *
 * Invalidates the run: both outcomes change what the list of documents is, and
 * a retry also changes whether anything is running.
 */
export const useResolveFailedRemediationItem = (runId: string) => {
  const invalidate = useRemediationInvalidation();
  const onError = useBackendErrorToast('app.remediation.toast.resolveFailed');

  return useMutation({
    mutationFn: resolveFailedItem,
    onSuccess: (_data, { action }) => {
      invalidate.documentsChanged(runId);
      // A retry puts the document back in the worker's hands, which can move the
      // run out of a status only a person could leave — so the hub has to start
      // watching again. Removing one settles nothing new.
      if (action === 'retry') invalidate.workDispatched();
    },
    onError,
  });
};

/**
 * Record what the user typed for the blanks the AI left.
 *
 * These are substituted into every document of the run when it is rendered at
 * finalize, so they must be sent before the run is finalized — the summary
 * screen is what triggers that, and this page is the step before it.
 *
 * The whole map each time, which is why there is no optimistic merge here: the
 * server replaces what it holds.
 */
export const useSaveRemediationPlaceholders = (runId: string) => {
  const invalidate = useRemediationInvalidation();
  const onError = useBackendErrorToast('app.remediation.toast.placeholdersFailed');

  return useMutation({
    mutationFn: savePlaceholders,
    // The values live on the run row and no item moves, so the item caches and
    // the list stand - which matters here, because this autosaves on a debounce.
    onSuccess: () => invalidate.runChanged(runId),
    onError,
  });
};

/**
 * Finalize: render every accepted item and fork assessment version N+1. Returns
 * 202 — the work happens on the worker, so the caller keeps polling the run for
 * `resultAssessmentId`.
 */
export const useFinalizeRemediationRun = () => {
  const invalidate = useRemediationInvalidation();
  const onError = useBackendErrorToast('app.remediation.toast.finalizeFailed');

  return useMutation({
    mutationFn: finalizeRun,
    // Finalizing is the one transition the user starts and the worker finishes —
    // the run leaves READY_FOR_REVIEW for FINALIZING, so the hub's watcher, which
    // had stopped once the run became the user's turn, has to pick it up again.
    // Every accepted item is rendered and the run closes, so the item caches go
    // too - they were left standing before, and a reviewer navigating back saw
    // the pre-finalize document list.
    onSuccess: (_data, runId) => {
      invalidate.documentsChanged(runId);
      invalidate.workDispatched();
    },
    onError,
  });
};
